// scheduling/pages/ClosedJobsTab.jsx
// Admin Console → "Closed Job Cleanup". Firm-wide sweep of jobs PE has completed that still
// hold future-dated schedule entries.
//
// Background: peSync used to delete a completed job's rprs.Jobs row AND every schedule entry
// on it — past and future, unaudited. That silently destroyed 418 entries / ~1,900h across 62
// jobs in a single month (the Ergon 2024 1120 close on 2026-08-26 alone took 84 entries /
// 287h from 8 staff). The sync now retains the job and marks it ClosedInPE instead, so the
// hours survive. This page is where whoever owns cleanup can see all of that stranded time in
// one place and clear it deliberately.
//
// Reuses ClosedJobsSection for the per-job preview/confirm so the admin sweep, My Jobs and
// My Schedule all behave identically. Past-dated entries are never deleted.

import { useState, useEffect, useCallback } from 'react';
import { fetchClosedJobsWithSchedule } from '../services/schedData';
import { deleteClosedJobFuture } from '../services/schedApi';
import ClosedJobsSection from '../components/dashboard/ClosedJobsSection';
import { BRAND } from '../utils/schedConstants';

export default function ClosedJobsTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setRows(await fetchClosedJobsWithSchedule({}) || []);
    } catch (e) {
      setError(e.message || 'Could not load closed jobs');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const clearClosedJobFuture = useCallback(async (jobId, opts = {}) => {
    const res = await deleteClosedJobFuture(jobId, opts);
    if (!opts.preview) await load();
    return res;
  }, [load]);

  const totalEntries = rows.reduce((a, j) => a + (j.FutureEntries || 0), 0);
  const totalHours = Math.round(rows.reduce((a, j) => a + Number(j.FutureHours || 0), 0) * 100) / 100;
  // Deliberately not a distinct head-count: the read returns per-job StaffCount, and
  // summing those would double-count anyone appearing on two closed jobs. The worst
  // single job is the useful signal here.
  const worstStaffCount = rows.reduce((a, j) => Math.max(a, j.StaffCount || 0), 0);

  return (
    <div style={{ background: '#fff', border: '1px solid #E5E7EB', borderRadius: 10, padding: 24, marginBottom: 20 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 4 }}>
        <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: BRAND.deepTeal }}>
          Closed Job Cleanup
        </h3>
        <button className="sched-closed-btn" onClick={load} disabled={loading}>
          {loading ? 'Loading…' : '↻ Refresh'}
        </button>
      </div>

      <p style={{ margin: '0 0 14px', fontSize: 12, color: '#6B7280', lineHeight: 1.5 }}>
        Jobs Practice Engine reports as completed that still hold <strong>future-dated</strong> scheduled
        time. The hours are real — peSync keeps them now rather than deleting them when a job closes —
        but nobody is working these jobs any more, so the time usually wants clearing or moving to the
        successor job. Past-dated entries are the job&rsquo;s history and are never touched.
      </p>

      {error && (
        <div style={{ fontSize: 12, color: '#B91C1C', marginBottom: 12 }}>{error}</div>
      )}

      {loading ? (
        <div style={{ fontSize: 12, color: '#6B7280' }}>Loading…</div>
      ) : rows.length === 0 ? (
        <div style={{
          fontSize: 12, color: '#065F46', background: '#ECFDF5',
          border: '1px solid #A7F3D0', borderRadius: 8, padding: '10px 12px',
        }}>
          Nothing stranded — no completed job is holding future-dated time.
        </div>
      ) : (
        <>
          <div style={{ fontSize: 12, color: '#374151', marginBottom: 10 }}>
            <strong>{rows.length}</strong> {rows.length === 1 ? 'job' : 'jobs'} &middot;{' '}
            <strong>{totalEntries}</strong> future {totalEntries === 1 ? 'entry' : 'entries'} &middot;{' '}
            <strong>{totalHours}h</strong>
            {worstStaffCount > 1 ? <> &middot; up to <strong>{worstStaffCount}</strong> people on one job</> : null}
          </div>
          <ClosedJobsSection
            closedJobs={rows} clearClosedJobFuture={clearClosedJobFuture}
            mode="manager" />
        </>
      )}
    </div>
  );
}
